import React, { FC } from 'react'
import { Box, makeStyles, TableCell, TableRow, Theme, Typography } from '@material-ui/core'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import IconDot from '../../assets/images/icons/Dot.svg?react'
import { LegalEntityDirector, LimitedAccessRight } from '../../graphql'
import { useDetermineUserRights, useMatchCurrentLocation } from '../../hooks'
import { APP_PATHS, PATH_PARAMS } from '../../routes/paths'
import { AlertMsgRequisites } from '../../types'
import { ActionButton } from '../Common'
import { CopyToClipboard } from '../Common/CopyToClipboard'

const useStyles = makeStyles((theme: Theme) => ({
  buttonsBox: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(0, -1),
    '& .MuiButton-root': {
      minWidth: 10,
      minHeight: 'auto',
      fontSize: '0.875rem',
      fontWeight: '400',
      lineHeight: '1.5rem',
    },
    '& .MuiButton-label': {
      textDecoration: 'underline',
    },
  },
  cell: {
    whiteSpace: 'nowrap',
  },
  mail: {
    color: 'inherit',
  },
  tableRow: {
    cursor: 'pointer',
  },
  noneBorderBottom: {
    '& td': {
      borderBottom: 'none',
    },
  },
}))

export const DirectorsTableRow: FC<{
  director: LegalEntityDirector
  watchDetails: (directorId: string | number) => () => void
  setOpenDelete: React.Dispatch<React.SetStateAction<string | number | undefined>>
  handleEdit: (directorId: string | number) => (e: React.SyntheticEvent) => void
  allowToDelete?: boolean
  hideBorderBottom?: boolean
}> = ({ director, watchDetails, hideBorderBottom, setOpenDelete, allowToDelete, handleEdit }) => {
  const { t } = useTranslation()
  const classes = useStyles()
  const { [PATH_PARAMS.applicationId]: applicationId } = useParams() as Record<string, string>

  const { userRights } = useDetermineUserRights(applicationId)
  const isViewOnly = userRights?.limitedAccessRight === LimitedAccessRight.ViewOnly

  const isDirectorsTab = useMatchCurrentLocation(APP_PATHS.dashboard.contractManagement.people)

  return (
    <TableRow
      hover
      data-test="personAccount"
      onClick={watchDetails(director.id)}
      className={clsx(
        isDirectorsTab ? classes.tableRow : '',
        hideBorderBottom && classes.noneBorderBottom,
      )}
    >
      <TableCell>
        <Typography noWrap>{director.individual?.fullName}</Typography>
      </TableCell>

      <TableCell className={classes.cell}>{t(director.role || '')}</TableCell>
      <TableCell className={classes.cell}>{t(director.signingRight || '')}</TableCell>
      <TableCell className={classes.cell}>
        <a
          className={classes.mail}
          href={`mailto:${director.individual?.email}`}
          onClick={(e) => e.stopPropagation()}
        >
          {director.individual?.email}
        </a>
      </TableCell>
      <TableCell>
        <CopyToClipboard address={director.individual?.phone} msgType={AlertMsgRequisites.phone} />
      </TableCell>

      <TableCell width={168}>
        {!isViewOnly && (
          <Box className={classes.buttonsBox}>
            <ActionButton aria-label="Edit" type="button" onClick={handleEdit(director.id)}>
              {t('edit', 'Edit')}
            </ActionButton>
            {allowToDelete && (
              <>
                <IconDot />
                <ActionButton
                  color="secondary"
                  aria-label="delete"
                  type="button"
                  onClick={() => setOpenDelete(director.id)}
                  data-test="personsAccountsDelete-btn"
                >
                  {t('delete', 'Delete')}
                </ActionButton>
              </>
            )}
          </Box>
        )}
      </TableCell>
    </TableRow>
  )
}
